import React, { useContext, useEffect } from 'react'
import { StoresContext } from '@App/contexts'
import { ExportType } from '@App/enum/export-type'
import Radio, { Option } from '@Components/Fields/Radio'
import Button from '@Components/Buttons/Button'
import { observer } from 'mobx-react'
import RegistrationHistoryReportRequestDTO from '@Entities/report/registration-history-report-request'
import { RegistrationHistoryTabName } from '@App/enum/registration-history-tab-name'
import { submitRegistrationHistoryReportRequest } from '@App/api/routes/search'
import { RegistrationStatus, RegistrationStatusAndFilters } from '@App/enum/registration-status'
import { regHistoryTabNamesAsRegistrationStatus } from '@App/helpers/reg-history-tab-names-to-source'
import { HistoryWorkQuery } from '@Entities/search'

interface ExportRegistrationHistoryFormProps {
    registrationHistoryTabName?: RegistrationHistoryTabName
    setIsFormSubmitted: (isFormSubmitted: boolean) => void
}

const ExportRegistrationHistoryForm = ({
    registrationHistoryTabName,
    setIsFormSubmitted,
}: ExportRegistrationHistoryFormProps) => {
    const [format, setFormat] = React.useState<string | undefined>(undefined)
    const { exportStore, preloaderStore, rightsholderStore, registrationHistoryStore } = useContext(StoresContext)

    const onFormatChange = (value: Option) => {
        setFormat(value.value)
    }

    const isFormValid = (): boolean => !!format

    const onSubmitCatalogExportRequest = () => {
        if (isFormValid()) {
            preloaderStore.openTransparentPreloader(true)
            const rightsholderId = rightsholderStore.currentRightsholderId
            const searchCriteria: HistoryWorkQuery = registrationHistoryStore.currentQuery
            const reportRequest: RegistrationHistoryReportRequestDTO = {
                isProcessing: registrationHistoryTabName === RegistrationHistoryTabName.Processing,
                format: format,
                publisherIPI: searchCriteria.publisherIpi,
                publisherName: searchCriteria.publisherName,
                recordingArtist: searchCriteria.artist,
                recordingISRC: searchCriteria.isrc,
                recordingTitle: searchCriteria.recordingName,
                workTitle: searchCriteria.title,
                writerIPI: searchCriteria.writerIpi,
                writerName: searchCriteria.writerName,
            }
            const useFilters = registrationHistoryTabName === RegistrationHistoryTabName.Completed
            const currentFilters = registrationHistoryStore.currentFilters
            if (useFilters && currentFilters) {
                const registrationStatus: RegistrationStatusAndFilters =
                    regHistoryTabNamesAsRegistrationStatus(registrationHistoryTabName)
                if (registrationStatus.status === RegistrationStatus.processing) {
                    reportRequest.workHistorySourceFilter = currentFilters.workHistorySourceFilter
                    reportRequest.workHistoryStatusFilter = currentFilters.workHistoryStatusFilter
                    reportRequest.workHistoryOverclaimFilter = currentFilters.workHistoryOverclaimFilter
                    if (reportRequest.workHistoryStatusFilter?.length === 0) {
                        reportRequest.workHistoryStatusFilter = registrationStatus.defaultFilters
                    }
                }
            }

            submitRegistrationHistoryReportRequest(rightsholderId, reportRequest)
                .then(() => {
                    setIsFormSubmitted(true)
                })
                .finally(() => preloaderStore.openTransparentPreloader(false))
        }
    }

    useEffect(() => {
        exportStore.setExportType(ExportType.REGISTRATION_HISTORY)
        exportStore.loadExportFormats().then()
    }, [])

    return (
        <>
            <Radio
                options={exportStore.exportFormats}
                field={{ name: 'File Type' }}
                direction="column"
                onChange={onFormatChange}
                title="File Type:"
            />
            <div className="download-button">
                <Button
                    mainColor
                    type="primary"
                    disabled={!isFormValid()}
                    onClick={() => onSubmitCatalogExportRequest()}
                >
                    Download
                </Button>
            </div>
        </>
    )
}

export default observer(ExportRegistrationHistoryForm)
